import { normalizeString } from '@asset-metrix/portal-ui';

import {
  dataFieldOptions,
  isFilterOnlyLimit,
  type LimitMode,
  type LimitParameter,
} from './createInvestmentLimitModel';

type DataFieldOption = (typeof dataFieldOptions)[number];

const kpiDataLevels = {
  available_capital: ['L1', 'L2'],
  cumulative_all_commitment: ['L1', 'L2', 'L3'],
  cumulative_all_cost: ['L1', 'L2', 'L3'],
  cumulative_all_paid_in_inside_commitment: ['L1', 'L2', 'L3'],
  cumulative_all_returned: ['L1', 'L2', 'L3'],
  days_to_final_termination_date: ['L2'],
  days_to_investment_end_date: ['L2'],
  days_to_termination_date: ['L2'],
  dpi: ['L2'],
  effective_valuation: ['L1', 'L2', 'L3'],
  exposure: ['L1', 'L2'],
  irr: ['L2'],
  rvpi: ['L2'],
  tvpi: ['L2'],
  undrawn_commitment: ['L1', 'L2'],
} satisfies Record<DataFieldOption, readonly string[]>;

const relativeExcludedKpis = new Set<DataFieldOption>([
  'dpi',
  'irr',
  'rvpi',
  'tvpi',
]);

const knownDataLevelIds = new Set(['L1', 'L2', 'L3']);

function normalizedDataLevelId(dataLevelId: string | null | undefined) {
  const trimmed = normalizeString(dataLevelId);
  return trimmed != null && knownDataLevelIds.has(trimmed) ? trimmed : null;
}

function dataLevelAllowsKpi(
  kpi: DataFieldOption,
  dataLevelId: string | null | undefined
) {
  const normalized = normalizedDataLevelId(dataLevelId);
  if (normalized == null) {
    return true;
  }

  return kpiDataLevels[kpi].includes(normalized);
}

/**
 * KPIs the limit expression may use for the current comparison and data levels.
 *
 * A null, blank, or unknown data level id does not restrict the list. Known
 * ids are `L1`, `L2`, and `L3`, matched after trimming. Relative limits also
 * drop `tvpi`, `dpi`, `irr`, and `rvpi`, and keep only KPIs allowed by both
 * the base data level and the denominator data level.
 */
export function getAvailableKpis(parameter: LimitParameter): DataFieldOption[] {
  const relative = parameter.valueType === 'relative';

  return dataFieldOptions.filter((kpi) => {
    if (relative && relativeExcludedKpis.has(kpi)) {
      return false;
    }
    if (!dataLevelAllowsKpi(kpi, parameter.dataLevelId)) {
      return false;
    }
    if (
      relative &&
      !dataLevelAllowsKpi(kpi, parameter.denominator?.dataLevelId)
    ) {
      return false;
    }

    return true;
  });
}

function selectedKpiFor(parameter: LimitParameter, limitMode: LimitMode) {
  return limitMode === 'Position' ? parameter.evalMetric : parameter.dataField;
}

export function isSelectedKpiAvailable(
  parameter: LimitParameter,
  limitMode: LimitMode
) {
  if (isFilterOnlyLimit(limitMode)) {
    return true;
  }

  const selected = selectedKpiFor(parameter, limitMode);
  const available = new Set<string>(getAvailableKpis(parameter));
  return selected != null && available.has(selected);
}

export function clearUnavailableKpis(
  parameter: LimitParameter,
  limitMode: LimitMode
): LimitParameter {
  if (isSelectedKpiAvailable(parameter, limitMode)) {
    return parameter;
  }

  return limitMode === 'Position'
    ? { ...parameter, evalMetric: null }
    : { ...parameter, dataField: null };
}
